"use client";

import {
  Ban,
  BookA,
  BookOpen,
  BookOpenText,
  ChevronRight,
  FileText,
  Layers3,
  ListChecks,
  MessageCircleQuestion,
  Search,
  Settings2,
  User,
  UserRound,
} from "lucide-react";

import { CommandItem } from "@/components/ui/command";
import {
  ContentTypeIcon,
  getContentMeta,
} from "@/components/learning-paths/contentMeta";
import IconTile from "@/components/ui/icon-tile";
import { Badge } from "@/components/ui/badge";
import { KeyCombo } from "@/components/ui/kbd";
import { pageShortcutKeys } from "@/lib/keyboard";
import { cn } from "@/lib/utils";

/** The icon for each result `kind` the search returns. */
export const KIND_ICON = {
  assignment: Layers3,
  content: Layers3,
  learning_path: BookOpen,
  resource: BookOpen,
  test: FileText,
  admin: Settings2,
  page: Search,
  faq: MessageCircleQuestion,
  glossary: BookA,
  objection: Ban,
  persona: UserRound,
  rubric: ListChecks,
  story: BookOpenText,
  person: User,
};

function ResultIcon({ result }) {
  if (result.contentType) {
    const meta = getContentMeta(result.contentType);
    return (
      <span
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
          meta.tile,
        )}
      >
        <ContentTypeIcon
          type={result.contentType}
          iconSize="h-4 w-4"
          tooltip={result.badge}
        />
      </span>
    );
  }

  const Icon = KIND_ICON[result.kind] || Search;
  return (
    <IconTile as="span" icon={Icon} size="md" tone="neutral" />
  );
}

export function LoadingRows() {
  return (
    <div className="space-y-2 px-3 py-3">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="flex items-center gap-3 rounded-lg border border-transparent px-2 py-2"
        >
          <div className="h-9 w-9 rounded-lg bg-gray-100" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded bg-gray-100" />
            <div className="h-2.5 w-2/3 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * One search result: icon, title and badge, the subtitle beneath, and the
 * page's shortcut keys when it has one.
 */
export default function NavSearchResultRow({ result, apple, onSelect }) {
  return (
    <CommandItem
      value={result.id}
      onSelect={onSelect}
      className="group cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 !text-gray-900 !opacity-100 transition-colors hover:bg-gray-50 aria-selected:bg-action-50/80 aria-selected:!text-gray-900 data-disabled:pointer-events-auto data-disabled:!opacity-100"
    >
      <ResultIcon result={result} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold !text-gray-900">
            {result.title}
          </span>
          {result.badge ? (
            <Badge tone="neutral" size="tag" className="shrink-0">
              {result.badge}
            </Badge>
          ) : null}
        </div>
        {result.subtitle ? (
          <p className="mt-0.5 truncate text-xs !text-gray-500">
            {result.subtitle}
          </p>
        ) : null}
      </div>
      {result.shortcut ? (
        <KeyCombo keys={pageShortcutKeys(result.shortcut, apple)} />
      ) : null}
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-all duration-200 group-aria-selected:translate-x-0.5 group-aria-selected:text-gray-400" />
    </CommandItem>
  );
}
